import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from '../../../../contexts/SocketContext';

/**
 * Integrates socket events with TanStack Query's cache
 * Updates query cache when socket events are received
 * @returns {Object} Socket-related utilities
 */
export const useSocketIntegration = (queryClient) => {
  const { socket, emitWithSourceId } = useSocket();
  // We now accept queryClient as a parameter instead of using the hook directly
  const localSocketIdRef = useRef(null);

  // Store the local socket ID for identifying local vs remote events
  useEffect(() => {
    if (!socket) return;
    localSocketIdRef.current = socket.id;
    
    console.log('[TanStack] Local socket ID stored:', socket.id);
  }, [socket]);

  // Setup socket event listeners that integrate with query cache
  useEffect(() => {
    if (!socket || !queryClient) return;
    
    // Handle lead updates
    const handleLeadUpdated = (data) => {
      // Check if this is a system event that should be ignored
      if (data._systemEvent === 'initialLoad') {
        console.log('[TanStack] Ignoring initial load system event');
        return;
      }
      
      // Check if this update came from the same device
      const sourceSocketId = data._sourceSocketId;
      if (sourceSocketId && sourceSocketId === localSocketIdRef.current) {
        console.log('[TanStack] Ignoring update from same device:', sourceSocketId);
        return;
      }
      
      console.log('[TanStack] 📥 RECEIVED SOCKET EVENT lead:updated:', data);
      
      // Handle both formats: direct lead object or {leadId, lead} format
      const updatedLead = data.lead || data;
      const businessLeadId = updatedLead.leadId;
      
      if (!businessLeadId) {
        console.warn('[TanStack] ⚠️ Received lead update without business leadId', updatedLead);
        return;
      }
      
      // Update the specific lead cache if we have it
      queryClient.setQueryData(['lead', businessLeadId], old => {
        if (!old) return old;
        return {
          ...old,
          lead: {
            ...old.lead,
            ...updatedLead,
            callCount: updatedLead.callCount !== undefined ? Number(updatedLead.callCount) : old.lead.callCount,
          }
        };
      });
      
      // Invalidate both leads queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['infiniteLeads'] });
      
      console.log('[TanStack] ✅ Updated query cache for lead:', businessLeadId);
    };
    
    // Handle stage changes
    const handleStageChanged = (data) => {
      const leadId = data.leadId;
      const newStage = data.stage || data.newStage; // Handle both formats
      
      if (!leadId) {
        console.warn('[TanStack] Received stage change without business leadId');
        return;
      }
      
      console.log('[TanStack] Received stage change via socket:', { 
        leadId, 
        newStage,
        stageName: data.stageName,
        timestamp: new Date().toLocaleTimeString()
      });
      
      // Update the specific lead cache if we have it
      queryClient.setQueryData(['lead', leadId], old => {
        if (!old) return old;
        return {
          ...old,
          lead: {
            ...old.lead,
            stage: newStage,
          }
        };
      });
      
      // Invalidate both leads queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['infiniteLeads'] });
    };
    
    // Handle new leads
    const handleLeadAdded = (data) => {
      console.log('[TanStack] New lead added via socket:', data);
      
      // Just invalidate the queries to trigger a refetch
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['infiniteLeads'] });
    };
    
    // Register socket event handlers
    socket.on('lead:updated', handleLeadUpdated);
    socket.on('stage:changed', handleStageChanged);
    socket.on('lead:added', handleLeadAdded);
    socket.on('call:added', handleLeadUpdated); // Call updates also update leads
    
    // Cleanup function to remove event listeners
    return () => {
      socket.off('lead:updated', handleLeadUpdated);
      socket.off('stage:changed', handleStageChanged);
      socket.off('lead:added', handleLeadAdded);
      socket.off('call:added', handleLeadUpdated);
    };
  }, [socket, queryClient]);
  
  return {
    socket,
    emitWithSourceId,
    localSocketId: localSocketIdRef.current,
  };
};

export default useSocketIntegration;
